import { Trans } from '@lingui/macro';
import type { InlineProductCardProduct } from '@we-make-websites/ui-lib/components';
import {
  Button,
  RecommendedEssentialCardMobile,
} from '@we-make-websites/ui-lib/src';
import type { StoreMoney } from '@we-make-websites/ui-lib/src';
import clsx from 'clsx';
import { useContext } from 'use-context-selector';
import { CheckoutButton } from 'components/checkoutButton/CheckoutButton';
import Overlay from 'components/overlay/Overlay';
import { OverlayContext } from 'components/overlay/OverlayContext';
import { InlineProductCardConnected } from 'components/productCard/InlineProductCardConnected';
import { useProduct } from 'components/productContext/ProductProvider';
import type { StoreMediaImage } from 'components/productContext/types/storeMedia';

import styles from './RecommendedEssentialsModal.module.scss';

const overlayNamespace = 'recommendedEssentials';

/**
 * RecommendedEssentialsModal Component.
 */
export const RecommendedEssentialsModal = ({
  products,
}: {
  products: InlineProductCardProduct[];
}) => {
  const { close } = useContext(OverlayContext);

  const footer = (
    <div className={styles.recommendedEssentialsModal__footer}>
      <CheckoutButton />

      <div className={styles.recommendedEssentialsModal__secondaryActions}>
        <Button modifiers={['text']} onClick={() => close(overlayNamespace)}>
          <Trans id="recommendedEssentials.continue">Continue Shopping</Trans>
        </Button>

        <Button modifiers={['text']} href={'/cart'}>
          <Trans id="recommendedEssentials.viewBag">view bag</Trans>
        </Button>
      </div>
    </div>
  );

  const title = (
    <p
      className={clsx(
        styles.recommendedEssentialsModal__title,
        'text-heading-h5-desktop text-heading-h5-mobile'
      )}
    >
      <Trans id="recommendedEssentials.modalTitle">
        The following has been added:
      </Trans>
    </p>
  );

  return (
    <Overlay
      type="mobile-drawer"
      blockClass="recommendedEssentialsOverlay"
      namespace={overlayNamespace}
      footer={footer}
      title={title}
    >
      <RecommendedEssentialsListMobile products={products} />
    </Overlay>
  );
};

const RecommendedEssentialsListMobile = ({
  products,
}: {
  products: InlineProductCardProduct[];
}) => {
  const productContext = useProduct();

  if (productContext === undefined)
    throw new Error(
      'RecommendedEssentialsListMobile must be used within a ProductContext'
    );

  const {
    title,
    productType,
    price,
    media,
    url = '',
    id,
    handle,
  } = productContext;

  // Get first image.
  const featuredMedia = media.find(
    (image) => image.type === 'IMAGE'
  ) as StoreMediaImage;

  const mainProduct: InlineProductCardProduct = {
    systemName: productType,
    title: title,
    image: featuredMedia.image.url,
    price: price as StoreMoney,
    availableForSale: true,
    handle,
    url,
    id,
  };

  return (
    <div className={styles.recommendedEssentialsModal__products}>
      <div>
        <div className={styles.recommendedEssentialsModal__cardWrapper}>
          <InlineProductCardConnected
            productCardComponent={RecommendedEssentialCardMobile}
            product={mainProduct}
            isMainProduct={true}
          />
        </div>
      </div>
      <fieldset className={styles.recommendedEssentialsModal__upSells}>
        <legend
          className={clsx(
            styles.recommendedEssentialsModal__upSellsLegend,
            'text-body-2-desktop text-body-2-mobile'
          )}
        >
          <Trans id="recommendedEssentials.upSellsLegend">
            Need any of these?
          </Trans>
        </legend>
        {products.map((product, productIndex) => {
          if (!product) return null;

          return (
            <div
              key={productIndex}
              className={styles.recommendedEssentialsModal__cardWrapper}
            >
              <InlineProductCardConnected
                productCardComponent={RecommendedEssentialCardMobile}
                product={{ ...product, systemName: '' }}
              />
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};
